import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";

export interface MenuItemModifierRef {
  mod_id: string;
  required?: boolean;
  minimum?: number;
  maximum?: number;
  selectionType?: string;
  status?: boolean;
}

export interface MenuItem {
  id: string;
  name: string;
  description?: string;
  priceCents: number;
  imageUrl?: string;
  isAvailable?: boolean;
  caloriesLabel?: string;
  modifiers?: MenuItemModifierRef[];
  categoryIds?: string[];
  posCategoryIds?: string[];
  // POS item id (from item.pos.id). Absent for an item with no POS link
  // (`pos: null`). Display/lookup only - never the order line identity.
  posItemId?: string;
  // The served menu item's Mongo `_id` (hex string). This is the cart line
  // identity sent to the backend as `item.guid` (FOYR-424 R15). Optional only
  // because a menu persisted before FOYR-424 lacks it; the bootstrap mapper
  // sets it for every served item carrying an `_id`.
  menuItemId?: string;
}

/**
 * The `item.guid` an order line carries for this menu item: always the menu
 * item's Mongo `_id`, never its POS id (FOYR-424 R15). Falls back to the
 * internal `id` only for a menu persisted before `menuItemId` existed - the
 * app refreshes the menu on every load, so that window is one render.
 */
export function orderLineGuid(item: Pick<MenuItem, "id" | "menuItemId">): string {
  return item.menuItemId || item.id;
}

export interface MenuCategory {
  id: string;
  name: string;
  itemIds: string[];
}

export interface ModificationItem {
  id: string;
  displayName: string;
  prices?: { id: string; price: number; name: string }[];
  image?: string;
  posId?: string;
}

export interface ModificationGroup {
  mod_id: string;
  posId?: string;
  name?: string;
  displayName: string;
  status?: boolean;
  data: ModificationItem[];
}

export interface MenuState {
  categories: MenuCategory[];
  itemsById: Record<string, MenuItem>;
  selectedCategoryId: string | null;
  items: MenuItem[];
  modificationsById: Record<string, ModificationGroup>;
  modificationsLoading: boolean;
}

const initialState: MenuState = {
  categories: [],
  itemsById: {},
  selectedCategoryId: null,
  items: [],
  modificationsById: {},
  modificationsLoading: false,
};

const menuSlice = createSlice({
  name: "menu",
  initialState,
  reducers: {
    clearMenu(state) {
      state.categories = [];
      state.itemsById = {};
      state.items = [];
      state.modificationsById = {};
      state.selectedCategoryId = null;
      state.modificationsLoading = false;
    },
    setMenu(
      state,
      action: PayloadAction<{
        categories: MenuCategory[];
        items: MenuItem[];
        itemsById: Record<string, MenuItem>;
        modificationsById?: Record<string, ModificationGroup>;
        selectedCategoryId?: string | null;
      }>,
    ) {
      state.categories = action.payload.categories;
      state.items = action.payload.items;
      state.itemsById = action.payload.itemsById;
      state.modificationsById = action.payload.modificationsById || {};
      state.selectedCategoryId =
        action.payload.selectedCategoryId ??
        action.payload.categories[0]?.id ??
        null;
    },
    setModificationsLoading(state, action: PayloadAction<boolean>) {
      state.modificationsLoading = action.payload;
    },
    setModifications(
      state,
      action: PayloadAction<Record<string, ModificationGroup>>,
    ) {
      // Merge new modifications with existing ones
      state.modificationsById = {
        ...state.modificationsById,
        ...action.payload,
      };
      state.modificationsLoading = false;
    },
    setSelectedCategory(state, action: PayloadAction<string>) {
      state.selectedCategoryId = action.payload;
    },
    upsertItems(
      state,
      action: PayloadAction<{ items: MenuItem[]; categoryId?: string }>,
    ) {
      for (const item of action.payload.items) {
        state.itemsById[item.id] = item;
      }
      if (action.payload.categoryId) {
        const cat = state.categories.find(
          (c) => c.id === action.payload.categoryId,
        );
        if (cat) {
          const ids = action.payload.items.map((i) => i.id);
          const set = new Set<string>([...cat.itemIds, ...ids]);
          cat.itemIds = Array.from(set);
        }
      }
    },
    addCategory(state, action: PayloadAction<MenuCategory>) {
      state.categories.push(action.payload);
    },
  },
});

export const {
  clearMenu,
  setMenu,
  setSelectedCategory,
  upsertItems,
  addCategory,
  setModificationsLoading,
  setModifications,
} = menuSlice.actions;
export default menuSlice.reducer;

// Selectors compatible with ScrollableTabs
export const selectMenu = (state: any) => state.menu as MenuState;

// The design always shows Drinks before Bites ("To drink" then "To start"),
// regardless of the order the backend returns categories in. This keeps the
// canonical section order stable while tolerating either input order.
const SECTION_ORDER = ["Drinks", "Bites"];

export const selectCategoryTabs = createSelector(selectMenu, (menu) => {
  const byName = new Map(menu.categories.map((c) => [c.name, c]));
  return SECTION_ORDER.filter((name) => byName.has(name)).map((name) => {
    const c = byName.get(name)!;
    return { label: c.name, value: c.id };
  });
});

export const selectSelectedCategoryId = createSelector(
  selectMenu,
  (menu) => menu.selectedCategoryId,
);

export const makeSelectItemsForCategory = (categoryId: string) =>
  createSelector(selectMenu, (menu) => {
    const cat = menu.categories.find((c) => c.id === categoryId);
    if (!cat) return [] as MenuItem[];
    return cat.itemIds.map((id) => menu.itemsById[id]).filter(Boolean);
  });

export const selectSelectedCategoryName = createSelector(
  selectMenu,
  (menu) =>
    menu.categories.find((c) => c.id === menu.selectedCategoryId)?.name ?? null,
);

// Get all items (flat list)
export const selectAllMenuItems = createSelector(
  selectMenu,
  (menu) => menu.items,
);
